import {
  Component,
  input,
  model,
  signal,
  computed,
  inject,
  ChangeDetectionStrategy,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import { SoCheckboxUI } from '../so-checkbox/so-checkbox.component';

export interface SoColumnPickerColumn {
  key: string;
  label: string;
  locked?: boolean;
}

@Component({
  selector: 'so-column-picker-ui',
  standalone: true,
  imports: [CommonModule, OverlayModule, SoCheckboxUI],
  templateUrl: './so-column-picker.component.html',
  styleUrl: './so-column-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoColumnPickerUI {
  private readonly overlay = inject(Overlay);

  columns = input.required<SoColumnPickerColumn[]>();
  hidden = model<string[]>([]);
  label = input<string>('Columnas');
  showCounter = input<boolean>(true);
  resetLabel = input<string>('Restablecer todas');

  protected isOpen = signal(false);

  protected scrollStrategy = this.overlay.scrollStrategies.close();

  protected overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  protected totalCount = computed(() => this.columns().length);
  protected shownCount = computed(() => this.totalCount() - this.hidden().length);

  toggleOpen(event: Event): void {
    event.stopPropagation();
    this.isOpen.update(open => !open);
  }

  closePanel(): void {
    this.isOpen.set(false);
  }

  isChecked(column: SoColumnPickerColumn): boolean {
    if (column.locked) return true;
    return !this.hidden().includes(column.key);
  }

  isLocked(column: SoColumnPickerColumn): boolean {
    return !!column.locked;
  }

  onToggleColumn(column: SoColumnPickerColumn, checked: boolean): void {
    if (column.locked) return;

    const current = this.hidden();
    if (checked) {
      this.hidden.set(current.filter(key => key !== column.key));
    } else if (!current.includes(column.key)) {
      this.hidden.set([...current, column.key]);
    }
  }

  onReset(): void {
    this.hidden.set([]);
  }

  onPanelKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    this.closePanel();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) this.closePanel();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isOpen()) this.closePanel();
  }
}
